import React, { useState, useEffect, Fragment } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import PaymentForm from './PaymentForm';
import { useNavigate } from 'react-router-dom';
import api, { errorMessage } from '../api';
import Navigation from '../lp2/Navigation';
import { CreditCard, Shield, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const isServerless = process.env.REACT_APP_SERVERLESS === 'true';
const stripePromise = isServerless
  ? loadStripe('pk_live_51IHEi8Db1bsMIykoILMWo270g6xUIUNhzcpCO4ExSBgy3gZpB785hYeVWBesrmf0VJibVuVIrWtETHBDO292OEMy00kuCBUxMd', { locale: 'de' })
  : loadStripe('pk_test_51IHEi8Db1bsMIykosP3NM48VcwtM2N3B9lfGXzw3Y1uCbf691oX1NzThg1RF6XM2fhlYU3uzmU3mJ37WbSAyDdI400HLIDWQGU', { locale: 'de' });

const PaymentPage = () => {
    const [clientSecret, setClientSecret] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userData, setUserData] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const createSubscription = async () => {
            try {
                const profileResponse = await api.get('/profile');
                setUserData(profileResponse.data);

                if (profileResponse.data.paymentStatus === 'paid') {
                    navigate('/profile');
                    return;
                }

                // Kunde und Preis ergeben sich serverseitig aus dem Token.
                // Vorher landete das Authorization-Objekt versehentlich im
                // Request-BODY statt in den Headern.
                const subscriptionResponse = await api.post('/create-subscription');

                setClientSecret(subscriptionResponse.data.clientSecret);
                setIsLoading(false);
            } catch (err) {
                if (err?.response?.status === 401) {
                    navigate('/login');
                    return;
                }
                console.error('Abo konnte nicht angelegt werden:', err);
                setError(errorMessage(err, 'Das Zahlungsformular konnte nicht geladen werden.'));
                setIsLoading(false);
            }
        };

        createSubscription();
    }, [navigate]);

    if (isLoading) {
        return (
            <Fragment>
                <Navigation />
                <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center px-4">
                    <div className="text-center">
                        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-6">
                            <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            Zahlungsformular wird vorbereitet
                        </h2>
                        <p className="text-gray-600">
                            Bitte warten Sie einen Moment...
                        </p>
                    </div>
                </div>
            </Fragment>
        );
    }

    if (error) {
        return (
            <Fragment>
                <Navigation />
                <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center px-4">
                    <div className="max-w-md w-full">
                        <div className="text-center">
                            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
                                <AlertCircle className="h-8 w-8 text-red-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                Fehler aufgetreten
                            </h2>
                            <p className="text-gray-600 mb-6">
                                {error}
                            </p>
                            <button
                                onClick={() => window.location.reload()}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                            >
                                Erneut versuchen
                            </button>
                        </div>
                    </div>
                </div>
            </Fragment>
        );
    }

    const options = {
        clientSecret,
        appearance: { 
            theme: 'stripe',
            variables: {
                colorPrimary: '#2563eb',
                colorBackground: '#ffffff',
                colorText: '#374151',
                colorDanger: '#dc2626',
                fontFamily: 'Inter, system-ui, sans-serif',
                spacingUnit: '4px',
                borderRadius: '8px',
            },
        },
    };

    return (
        <Fragment>
            <Navigation />
            <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-2xl mx-auto">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                            <CreditCard className="h-6 w-6 text-blue-600" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-900 mb-2">
                            Registrierung vervollständigen
                        </h2>
                        <p className="text-gray-600">
                            Sichere Zahlung mit Stripe
                        </p>
                    </div>

                    {/* Security Notice */}
                    <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start">
                        <Shield className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                        <div>
                            <h3 className="text-sm font-semibold text-green-800 mb-1">
                                Sichere Zahlung
                            </h3>
                            <p className="text-sm text-green-700">
                                Ihre Zahlungsdaten werden verschlüsselt übertragen und sicher verarbeitet.
                            </p>
                        </div>
                    </div>

                    {/* Payment Form */}
                    {clientSecret && (
                        <div className="bg-white rounded-xl shadow-lg p-8">
                            <Elements stripe={stripePromise} options={options}>
                                <PaymentForm clientSecret={clientSecret} currency={userData?.currency || 'CHF'} />
                            </Elements>
                        </div>
                    )}

                    {/* Trust Indicators */}
                    <div className="mt-8 text-center">
                        <div className="flex items-center justify-center space-x-6 text-gray-400">
                            <div className="flex items-center">
                                <Shield className="h-4 w-4 mr-2" />
                                <span className="text-sm">SSL Verschlüsselung</span>
                            </div>
                            <div className="flex items-center">
                                <CheckCircle className="h-4 w-4 mr-2" />
                                <span className="text-sm">PCI DSS Konform</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default PaymentPage;